/**
 * CHART PROPORTIONS — shared, because they are ratios rather than sizes.
 *
 * The metric/semantic split everywhere else in this package says sizes are
 * per-platform: 12px base type on a desktop panel, 15px on a phone. These are
 * different. A candle body that occupies 70% of its slot occupies 70% of its
 * slot on any screen, so unlike spacing there is no reason for the phone to
 * disagree — and every reason not to, since a chart that looks proportionally
 * different across devices is the fastest way to make two renderers look like
 * two products.
 *
 * Density still differs, and should: the phone shows far fewer bars. That is
 * `defaultBarCount`, derived from screen width, and it is not here.
 *
 * The `Px` suffix marks the handful of values that are genuinely pixels rather
 * than ratios. They are floors — the point below which a thing stops being
 * visible at all — and are the same on both platforms for that reason.
 *
 * ## What is deliberately NOT in here
 *
 * **Tick size and price precision.** They come from the instrument master, per
 * CLAUDE.md, and a token would be a second place for them to be wrong. The chart
 * hard-coded `minMove: 0.25` — E-mini S&P's tick — until this file was written;
 * fourteen of the eighteen listed contracts have a different one, so crude oil's
 * price axis was snapping to quarter-dollars against a real tick of a cent.
 *
 * **Hit-test tolerances.** In `packages/core/src/drawings/model.ts`. How close a
 * tap counts as hitting a line is interaction correctness, not visual style, and
 * a design pass changing it would change what a drag selects.
 */

export const chart = {
  /**
   * Candle body width as a share of its slot.
   *
   * 0.7 leaves a visible gap at every zoom. Filling the slot makes a dense chart
   * read as a solid block, which is the most common way a hand-rolled candle
   * renderer looks wrong beside a real one.
   */
  candleBodyRatio: 0.7,

  /**
   * Narrowest a candle body may be drawn.
   *
   * Below about a pixel the body vanishes on a non-retina density and the series
   * looks like it has gaps. Rarely binds on a phone; binds regularly on a
   * one-minute chart of a full session on a monitor.
   */
  minCandleWidthPx: 1,

  wickWidthPx: 1,

  /**
   * Headroom above the high and below the low, as a share of the range.
   *
   * A series drawn flush to the edges looks clipped, and an annotation just above
   * the session high would be invisible. The two renderers disagreed before this
   * — 0.08 on mobile against 0.1 on web — which is exactly the silent drift a
   * shared value exists to stop.
   */
  priceScalePadding: 0.1,

  /** Share of the plot height given to the volume histogram. */
  volumePaneRatio: 0.18,

  /** Empty slots kept to the right of the last bar, so it is not against the axis. */
  rightOffsetBars: 8,

  /**
   * Roughly how many horizontal gridlines to aim for.
   *
   * A hint, not a promise: the actual count is whatever a "nice" increment — 1,
   * 2, 2.5 or 5 times a power of ten — produces across the range, so labels read
   * 5,430 / 5,440 / 5,450 rather than 5,431.7 / 5,443.2.
   */
  gridLineTarget: 5,

  /**
   * The user's own drawings.
   *
   * Web-only today; mobile displays drawings rather than creating them. Shared
   * anyway, because the day mobile renders them these are the numbers that make
   * a trendline the same weight on both.
   */
  drawing: {
    lineWidthPx: 1.5,
    selectedLineWidthPx: 2,
    /** Dash pattern for a Fibonacci level and other non-solid marks. */
    dash: [4, 3],
    /** Fill opacity for a rectangle or a zone. */
    zoneOpacity: 0.1,
    /** Fib levels between 0 and 1 are dimmer, so the anchors read as anchors. */
    fibIntermediateOpacity: 0.6,
  },
} as const;

export type ChartTokens = typeof chart;
